/**
 * Winning-hand analysis for the milestone: the standard shape only,
 * four sets (run or triplet) plus one pair. No regional scoring, no special
 * hands (seven pairs, thirteen orphans and friends are deliberately absent).
 */

import type { HandGroup, WinningHand } from "./types.js";
import {
  COPIES_PER_KIND,
  TILE_KINDS,
  countKinds,
  kindAtIndex,
  kindIndex,
} from "./tiles.js";
import type { TileKind } from "./tiles.js";

const HONOR_START_INDEX = 27;

function countAt(counts: readonly number[], index: number): number {
  return counts[index] ?? 0;
}

/** A run needs index, index+1 and index+2 inside the same numbered suit. */
function canStartRunAtIndex(index: number): boolean {
  return index < HONOR_START_INDEX && index % 9 <= 6;
}

function makeTripletGroup(index: number): HandGroup {
  const kind = kindAtIndex(index);
  return { kind: "pong", tiles: [kind, kind, kind] };
}

function makeRunGroup(index: number): HandGroup {
  return {
    kind: "chow",
    tiles: [kindAtIndex(index), kindAtIndex(index + 1), kindAtIndex(index + 2)],
  };
}

/**
 * Backtracking decomposition into exactly `remaining` sets.
 * `counts` is mutated in place and always restored before returning.
 */
function extractSets(counts: number[], remaining: number): HandGroup[] | null {
  if (remaining === 0) return [];

  let index = -1;
  for (let i = 0; i < counts.length; i += 1) {
    if (countAt(counts, i) > 0) {
      index = i;
      break;
    }
  }
  if (index === -1) return null;

  if (countAt(counts, index) >= 3) {
    counts[index] = countAt(counts, index) - 3;
    const rest = extractSets(counts, remaining - 1);
    counts[index] = countAt(counts, index) + 3;
    if (rest) return [makeTripletGroup(index), ...rest];
  }

  if (
    canStartRunAtIndex(index) &&
    countAt(counts, index + 1) > 0 &&
    countAt(counts, index + 2) > 0
  ) {
    counts[index] = countAt(counts, index) - 1;
    counts[index + 1] = countAt(counts, index + 1) - 1;
    counts[index + 2] = countAt(counts, index + 2) - 1;
    const rest = extractSets(counts, remaining - 1);
    counts[index] = countAt(counts, index) + 1;
    counts[index + 1] = countAt(counts, index + 1) + 1;
    counts[index + 2] = countAt(counts, index + 2) + 1;
    if (rest) return [makeRunGroup(index), ...rest];
  }

  return null;
}

/**
 * Returns the decomposition of a complete hand, or null when the tiles are not
 * a standard win. `declaredMelds` is the number of already exposed melds, so
 * the same function keeps working once claims exist.
 */
export function analyzeStandardHand(
  kinds: readonly TileKind[],
  declaredMelds = 0,
): WinningHand | null {
  const requiredSets = 4 - declaredMelds;
  if (requiredSets < 0) return null;
  if (kinds.length !== requiredSets * 3 + 2) return null;

  const counts = countKinds(kinds);
  for (const count of counts) {
    if (count > COPIES_PER_KIND) return null;
  }

  for (let index = 0; index < counts.length; index += 1) {
    if (countAt(counts, index) < 2) continue;
    counts[index] = countAt(counts, index) - 2;
    const sets = extractSets(counts, requiredSets);
    counts[index] = countAt(counts, index) + 2;
    if (sets) {
      return { melds: sets, pair: kindAtIndex(index) };
    }
  }
  return null;
}

export function isStandardWinningHand(
  kinds: readonly TileKind[],
  declaredMelds = 0,
): boolean {
  return analyzeStandardHand(kinds, declaredMelds) !== null;
}

/**
 * Tiles that would complete the hand if drawn next ("waits").
 * Used for ready/tenpai hints and by the bot to protect a ready hand.
 */
export function findWaits(
  kinds: readonly TileKind[],
  declaredMelds = 0,
): TileKind[] {
  const requiredSets = 4 - declaredMelds;
  if (kinds.length !== requiredSets * 3 + 1) return [];

  const waits: TileKind[] = [];
  const counts = countKinds(kinds);
  for (const kind of TILE_KINDS) {
    if (countAt(counts, kindIndex(kind)) >= COPIES_PER_KIND) continue;
    if (analyzeStandardHand([...kinds, kind], declaredMelds)) waits.push(kind);
  }
  return waits;
}
